import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorEl, anchorMissing } from './tasks-help.shared';

/**
 * ?-Hilfe fuer den Anlegen-Dialog im Modus „Auftrag mit Schritten“ (08.10.2026).
 *
 * Das ? im Dialog waehlt die Tour nach dem Modus: Einzelaufgabe → `task-sheet-help`,
 * Auftrag → diese. Beide Formulare teilen sich kaum ein Feld (Kopf, Schritt-Editor,
 * Ablaufwahl gibt es nur hier); eine gemeinsame Tour haette mehr als elf Stationen
 * gebraucht und die Haelfte davon jedes Mal ueberspringen muessen.
 *
 * Traegt drei Kernaussagen:
 *  - **Ein Auftrag ist eine Kette von Aufgaben.** Jeder Schritt ist eine eigene Aufgabe
 *    fuer eine Person oder einen Bereich; ist er fertig, ist der naechste dran.
 *  - **Je Schritt drei Pflichtangaben** — was, wer, Beschreibung (`validateProjectDraft`).
 *  - **Gespeichert wird in einem Zug** (`submitProject`, ein Aufruf): ein Fehler legt
 *    nichts an.
 *
 * Die Schritt-Anker sitzen nur am OFFENEN Schritt (`chain-step-editor.component.ts`);
 * der Editor klappt beim Oeffnen des Modus den ersten unvollstaendigen auf, es steht also
 * immer einer offen, solange es Schritte gibt. Schalterabhaengige Stationen (Ablaufwahl,
 * Art, Faelligkeit) springen per `skipIf` — aus heisst unsichtbar (E-05).
 *
 * Start aus dem Hilfe-Hub: der Dialog oeffnet als Einzelaufgabe; `ensureProjectMode`
 * schaltet vor der ersten Station auf „Auftrag mit Schritten“ um.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

function settle(ms = 260): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Stellt den Umschalter auf „Auftrag mit Schritten“ (zweite Kachel), falls er auf
 * „Einzelne Aufgabe“ steht. Idempotent; ohne Umschalter (Bearbeiten, Schalter aus) tut
 * er nichts.
 */
export async function ensureProjectMode(): Promise<void> {
  const tiles = anchorEl(AnchorId.TaskSheetKind)?.querySelectorAll<HTMLElement>('[role="radio"]');
  const project = tiles?.[1];
  if (!project || project.getAttribute('aria-checked') === 'true') return;
  project.click();
  await settle();
}

/** Gibt es „Parallel zu Schritt …“? Nur mit Abhaengigkeiten UND parallelen Schritten. */
export function projectSheetHasParallel(): boolean {
  return !!anchorEl(AnchorId.TaskProjectStepAdd)?.querySelector('[data-add-parallel]');
}

/** Text- und Tonspur von `ps-add` — ohne und mit „Parallel zu Schritt …“. */
export const PROJECT_SHEET_ADD_VARIANTS = {
  base: { suffix: '', audioStepId: 'ps-add' },
  parallel: { suffix: '_PARALLEL', audioStepId: 'ps-add-parallel' },
} as const;

function addVariant() {
  return projectSheetHasParallel()
    ? PROJECT_SHEET_ADD_VARIANTS.parallel
    : PROJECT_SHEET_ADD_VARIANTS.base;
}

export const TASK_PROJECT_SHEET_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskProjectSheetHelp,
  version: 1,
  title: 'TASK_PROJECT_SHEET_HELP_TOUR_TITLE',
  description: 'TASK_PROJECT_SHEET_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: {
    route: '/tasks/overview',
    opens: { openerAnchor: AnchorId.TasksCreateButton, settleMs: 400 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 190,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_PROJECT_SHEET_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'ps-welcome',
          anchor: AnchorId.TaskSheetDialog,
          spotlightAnchor: AnchorId.TaskSheetDialog,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.WELCOME.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            ...dialogStep,
            waitForOpen: true,
            outsideDialog: true,
            highlightDialogHeader: true,
          },
          onBeforeShow: () => ensureProjectMode(),
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'ps-head',
          anchor: AnchorId.TaskProjectSheetHead,
          spotlightAnchor: AnchorId.TaskProjectSheetHead,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.HEAD.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.HEAD.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          onBeforeShow: () => ensureProjectMode(),
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          // Nur mit „Vorlagen“, dem Recht ManageRoutines und mindestens einem Ablauf.
          id: 'ps-template',
          anchor: AnchorId.TaskProjectSheetTemplate,
          spotlightAnchor: AnchorId.TaskProjectSheetTemplate,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.TEMPLATE.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.TEMPLATE.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectSheetTemplate),
        },
        {
          id: 'ps-step',
          anchor: AnchorId.TaskProjectStepCard,
          spotlightAnchor: AnchorId.TaskProjectStepCard,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.STEP.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.STEP.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepCard),
        },
        {
          id: 'ps-fields',
          anchor: AnchorId.TaskProjectStepTarget,
          spotlightAnchor: AnchorId.TaskProjectStepTarget,
          additionalSpotlightAnchors: [AnchorId.TaskProjectStepDescription],
          title: 'TUTORIAL.TASK_PROJECT_SHEET.FIELDS.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.FIELDS.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepTarget),
        },
        {
          // Nur mit „Schritt-Typen“.
          id: 'ps-kind',
          anchor: AnchorId.TaskProjectStepKind,
          spotlightAnchor: AnchorId.TaskProjectStepKind,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.KIND.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.KIND.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepKind),
        },
        {
          id: 'ps-waits',
          anchor: AnchorId.TaskProjectStepWaits,
          spotlightAnchor: AnchorId.TaskProjectStepWaits,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.WAITS.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.WAITS.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepWaits),
        },
        {
          // Nur mit „Soll-Zeiten und Fälligkeit je Schritt“.
          id: 'ps-plan',
          anchor: AnchorId.TaskProjectStepPlan,
          spotlightAnchor: AnchorId.TaskProjectStepPlan,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.PLAN.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.PLAN.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepPlan),
        },
        {
          id: 'ps-options',
          anchor: AnchorId.TaskProjectStepOptions,
          spotlightAnchor: AnchorId.TaskProjectStepOptions,
          additionalSpotlightAnchors: [AnchorId.TaskProjectStepChecklist],
          title: 'TUTORIAL.TASK_PROJECT_SHEET.OPTIONS.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.OPTIONS.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepOptions),
        },
        {
          // Zwei Texte: mit „Parallel zu Schritt …“ (Abhaengigkeiten UND parallele Schritte
          // an) und ohne. Der Sprechtext nennt den Knopf nur, wenn er dasteht.
          id: 'ps-add',
          anchor: AnchorId.TaskProjectStepAdd,
          spotlightAnchor: AnchorId.TaskProjectStepAdd,
          title: 'TUTORIAL.TASK_PROJECT_SHEET.ADD.TITLE',
          content: () => `TUTORIAL.TASK_PROJECT_SHEET.ADD.CONTENT${addVariant().suffix}`,
          audioStepId: () => addVariant().audioStepId,
          placement: 'top',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskProjectStepAdd),
        },
        {
          id: 'ps-save',
          anchor: AnchorId.TaskSheetSave,
          spotlightAnchor: AnchorId.TaskSheetSave,
          additionalSpotlightAnchors: [AnchorId.TaskSheetVisibility],
          title: 'TUTORIAL.TASK_PROJECT_SHEET.SAVE.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_SHEET.SAVE.CONTENT',
          // -v2 (09.10.2026): „ein Blick in die Fußzeile" statt „nach links" (Telefon: darüber).
          audioStepId: 'ps-save-v2',
          placement: 'top',
          align: 'end',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
